import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { APP_VERSION } from '@/version';

interface AdminAuthContextType {
  isAdminAuthenticated: boolean;
  login: () => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Kontrola admin oprávnění při načtení
  useEffect(() => {
    const checkAdminAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const storedVersion = localStorage.getItem('app_version');
      
      // Check if app version has changed
      if (storedVersion && storedVersion !== APP_VERSION) {
        console.log('Detekována nová verze aplikace, probíhá odhlášení admin uživatele...');
        setIsAdminAuthenticated(false);
        setIsLoading(false);
        return;
      }
      
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/admin/auth/verify', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setIsAdminAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Admin auth check error:', error);
      }
      
      setIsLoading(false);
    };

    checkAdminAuth();
  }, []);

  const login = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        return false;
      }

      const response = await fetch('/api/admin/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIsAdminAuthenticated(true);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Admin login error:', error);
      return false;
    }
  };

  const logout = () => {
    setIsAdminAuthenticated(false);
  };

  const value: AdminAuthContextType = {
    isAdminAuthenticated,
    login,
    logout,
    isLoading,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = (): AdminAuthContextType => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}; 